/**
 * Power Platform Admin tools (23 tools)
 * Environment lifecycle, DLP policies, managed environments, capacity/billing
 */

import { z } from "zod";
import type { Tool } from "@modelcontextprotocol/sdk/types.js";
import type { PowerPlatformAdminApi } from "../api/power-platform-admin-api.js";
import { logger } from "../utils/logger.js";
import { ConfirmationRequiredError } from "../utils/errors.js";

// ============================================================================
// Environment Lifecycle Tools (8)
// ============================================================================

export const getEnvironmentTool: Tool = {
  name: "get_environment",
  description: "Get detailed information about a Power Platform environment including Dataverse URL, region, and SKU.",
  inputSchema: {
    type: "object",
    properties: {
      environment: { type: "string", description: "Environment GUID. Uses default if omitted." },
    },
  },
};

export const createEnvironmentTool: Tool = {
  name: "create_environment",
  description: "Create a new Power Platform environment. Requires admin role.",
  inputSchema: {
    type: "object",
    properties: {
      displayName: { type: "string", description: "Display name for the environment" },
      location: { type: "string", description: "Azure region (e.g., 'unitedstates', 'europe')" },
      environmentSku: {
        type: "string",
        enum: ["Production", "Sandbox", "Trial"],
        description: "Environment type",
      },
      description: { type: "string", description: "Optional description" },
      currencyName: { type: "string", description: "Currency code (default: USD)" },
    },
    required: ["displayName", "location", "environmentSku"],
  },
};

export const deleteEnvironmentTool: Tool = {
  name: "delete_environment",
  description: "Delete a Power Platform environment permanently. This action cannot be undone. Set confirm=true.",
  inputSchema: {
    type: "object",
    properties: {
      environmentId: { type: "string", description: "Environment GUID to delete" },
      confirm: { type: "boolean", description: "Set to true to confirm deletion" },
    },
    required: ["environmentId"],
  },
};

export const backupEnvironmentTool: Tool = {
  name: "backup_environment",
  description: "Create a backup of a Power Platform environment.",
  inputSchema: {
    type: "object",
    properties: {
      environmentId: { type: "string", description: "Environment GUID to backup" },
      label: { type: "string", description: "Label for the backup" },
      notes: { type: "string", description: "Optional notes" },
    },
    required: ["environmentId", "label"],
  },
};

export const restoreEnvironmentTool: Tool = {
  name: "restore_environment",
  description: "Restore a Power Platform environment from a backup. Set confirm=true.",
  inputSchema: {
    type: "object",
    properties: {
      environmentId: { type: "string", description: "Source environment GUID" },
      backupId: { type: "string", description: "Backup ID to restore from" },
      targetEnvironmentId: { type: "string", description: "Target environment (defaults to source)" },
      confirm: { type: "boolean", description: "Set to true to confirm restore" },
    },
    required: ["environmentId", "backupId"],
  },
};

export const copyEnvironmentTool: Tool = {
  name: "copy_environment",
  description: "Copy a Power Platform environment to create a new one. Set confirm=true.",
  inputSchema: {
    type: "object",
    properties: {
      sourceEnvironmentId: { type: "string", description: "Source environment GUID" },
      targetDisplayName: { type: "string", description: "Display name for the copy" },
      targetLocation: { type: "string", description: "Azure region for the copy" },
      copyType: {
        type: "string",
        enum: ["MinimalCopy", "FullCopy"],
        description: "Copy type (default: FullCopy)",
      },
      confirm: { type: "boolean", description: "Set to true to confirm copy" },
    },
    required: ["sourceEnvironmentId", "targetDisplayName", "targetLocation"],
  },
};

export const resetEnvironmentTool: Tool = {
  name: "reset_environment",
  description: "Reset a Power Platform environment to its initial state. THIS DELETES ALL DATA. Set confirm=true.",
  inputSchema: {
    type: "object",
    properties: {
      environmentId: { type: "string", description: "Environment GUID to reset" },
      friendlyName: { type: "string", description: "New name after reset" },
      currencyName: { type: "string", description: "Currency code (default: USD)" },
      purpose: { type: "string", description: "Purpose description" },
      confirm: { type: "boolean", description: "Set to true to confirm reset" },
    },
    required: ["environmentId", "friendlyName"],
  },
};

export const listEnvironmentBackupsTool: Tool = {
  name: "list_environment_backups",
  description: "List available backups for a Power Platform environment.",
  inputSchema: {
    type: "object",
    properties: {
      environmentId: { type: "string", description: "Environment GUID" },
    },
    required: ["environmentId"],
  },
};

// ============================================================================
// DLP Policy Tools (6)
// ============================================================================

export const listDlpPoliciesTool: Tool = {
  name: "list_dlp_policies",
  description: "List all Data Loss Prevention (DLP) policies in the tenant. Requires admin role.",
  inputSchema: { type: "object", properties: {} },
};

export const getDlpPolicyTool: Tool = {
  name: "get_dlp_policy",
  description: "Get details of a DLP policy including connector group assignments.",
  inputSchema: {
    type: "object",
    properties: {
      policyId: { type: "string", description: "DLP policy ID (GUID)" },
    },
    required: ["policyId"],
  },
};

export const createDlpPolicyTool: Tool = {
  name: "create_dlp_policy",
  description: "Create a new DLP policy. Requires admin role.",
  inputSchema: {
    type: "object",
    properties: {
      displayName: { type: "string", description: "Policy display name" },
      environmentType: {
        type: "string",
        enum: ["AllEnvironments", "ExceptEnvironments", "OnlyEnvironments"],
        description: "Which environments the policy applies to",
      },
      connectorGroups: {
        type: "array",
        description: "Connector group assignments",
        items: {
          type: "object",
          properties: {
            classification: { type: "string", enum: ["Business", "NonBusiness", "Blocked"] },
            connectors: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: { type: "string" },
                  type: { type: "string" },
                },
                required: ["id"],
              },
            },
          },
          required: ["classification", "connectors"],
        },
      },
      environments: {
        type: "array",
        description: "Environments to include/exclude",
        items: {
          type: "object",
          properties: { name: { type: "string" } },
          required: ["name"],
        },
      },
    },
    required: ["displayName", "environmentType", "connectorGroups"],
  },
};

export const updateDlpPolicyTool: Tool = {
  name: "update_dlp_policy",
  description: "Update an existing DLP policy. Requires admin role.",
  inputSchema: {
    type: "object",
    properties: {
      policyId: { type: "string", description: "DLP policy ID (GUID)" },
      displayName: { type: "string", description: "New display name" },
      environmentType: {
        type: "string",
        enum: ["AllEnvironments", "ExceptEnvironments", "OnlyEnvironments"],
      },
      connectorGroups: { type: "array", description: "Updated connector groups" },
      environments: { type: "array", description: "Updated environment list" },
    },
    required: ["policyId"],
  },
};

export const deleteDlpPolicyTool: Tool = {
  name: "delete_dlp_policy",
  description: "Delete a DLP policy. Set confirm=true to proceed. Requires admin role.",
  inputSchema: {
    type: "object",
    properties: {
      policyId: { type: "string", description: "DLP policy ID (GUID)" },
      confirm: { type: "boolean", description: "Set to true to confirm deletion" },
    },
    required: ["policyId"],
  },
};

export const getDlpConnectorConfigsTool: Tool = {
  name: "get_dlp_connector_configs",
  description: "Get connector-level configurations for a DLP policy (endpoint filtering, etc.).",
  inputSchema: {
    type: "object",
    properties: {
      policyId: { type: "string", description: "DLP policy ID (GUID)" },
    },
    required: ["policyId"],
  },
};

// ============================================================================
// Managed Environment Tools (4)
// ============================================================================

export const getManagedEnvironmentSettingsTool: Tool = {
  name: "get_managed_environment_settings",
  description: "Get the governance configuration for a managed environment.",
  inputSchema: {
    type: "object",
    properties: {
      environment: { type: "string", description: "Environment GUID. Uses default if omitted." },
    },
  },
};

export const enableManagedEnvironmentTool: Tool = {
  name: "enable_managed_environment",
  description: "Enable managed environment features. Set confirm=true. Requires admin role.",
  inputSchema: {
    type: "object",
    properties: {
      environmentId: { type: "string", description: "Environment GUID" },
      protectionLevel: {
        type: "string",
        enum: ["Basic", "Standard", "Premium"],
        description: "Protection level (default: Standard)",
      },
      confirm: { type: "boolean", description: "Set to true to confirm" },
    },
    required: ["environmentId"],
  },
};

export const disableManagedEnvironmentTool: Tool = {
  name: "disable_managed_environment",
  description: "Disable managed environment features. Set confirm=true. Requires admin role.",
  inputSchema: {
    type: "object",
    properties: {
      environmentId: { type: "string", description: "Environment GUID" },
      confirm: { type: "boolean", description: "Set to true to confirm" },
    },
    required: ["environmentId"],
  },
};

export const updateManagedEnvironmentSettingsTool: Tool = {
  name: "update_managed_environment_settings",
  description: "Update governance settings for a managed environment. Requires admin role.",
  inputSchema: {
    type: "object",
    properties: {
      environmentId: { type: "string", description: "Environment GUID" },
      protectionLevel: {
        type: "string",
        enum: ["Basic", "Standard", "Premium"],
        description: "New protection level",
      },
      settings: {
        type: "object",
        description: "Extended settings key-value pairs",
      },
    },
    required: ["environmentId"],
  },
};

// ============================================================================
// Capacity & Licensing Tools (5)
// ============================================================================

export const getTenantCapacityTool: Tool = {
  name: "get_tenant_capacity",
  description: "Storage consumption across the whole tenant (Database, File, Log), summed from every environment's capacity record — the same source Microsoft's daily capacity report uses. Purchased entitlements are not exposed by the API.",
  inputSchema: {
    type: "object",
    properties: {
      tenantId: { type: "string", description: "Ignored — the tenant is the one you signed in to. Kept for compatibility." },
    },

  },
};

export const getEnvironmentCapacityTool: Tool = {
  name: "get_environment_capacity",
  description: "Get capacity consumption for a specific environment.",
  inputSchema: {
    type: "object",
    properties: {
      environment: { type: "string", description: "Environment GUID. Uses default if omitted." },
    },
  },
};

export const listBillingPoliciesTool: Tool = {
  name: "list_billing_policies",
  description: "List pay-as-you-go billing policies for the tenant. Requires admin role.",
  inputSchema: {
    type: "object",
    properties: {
      tenantId: { type: "string", description: "Ignored — the tenant is the one you signed in to. Kept for compatibility." },
    },

  },
};

export const getBillingPolicyTool: Tool = {
  name: "get_billing_policy",
  description: "Get details of a specific billing policy.",
  inputSchema: {
    type: "object",
    properties: {
      tenantId: { type: "string", description: "Ignored — the tenant is the one you signed in to. Kept for compatibility." },
      policyId: { type: "string", description: "Billing policy ID (GUID)" },
    },
    required: ["policyId"],
  },
};

export const getApiRequestSummaryTool: Tool = {
  name: "get_api_request_summary",
  description: "Explains where Power Platform request (API call) consumption can be read. No public API exposes it, so this tool reports that honestly instead of a number.",
  inputSchema: {
    type: "object",
    properties: {
      tenantId: { type: "string", description: "Ignored — the tenant is the one you signed in to. Kept for compatibility." },
    },

  },
};

// ============================================================================
// Zod Schemas
// ============================================================================

const guidString = z.string().min(1).max(100);

const envOptionalSchema = z.object({ environment: guidString.optional() });
const envRequiredSchema = z.object({ environmentId: guidString });

const createEnvSchema = z.object({
  displayName: z.string().min(1).max(500),
  location: z.string().min(1).max(100),
  environmentSku: z.enum(["Production", "Sandbox", "Trial"]),
  description: z.string().max(2000).optional(),
  currencyName: z.string().max(10).optional(),
});

const deleteEnvSchema = z.object({
  environmentId: guidString,
  confirm: z.boolean().optional().default(false),
});

const backupEnvSchema = z.object({
  environmentId: guidString,
  label: z.string().min(1).max(500),
  notes: z.string().max(2000).optional(),
});

const restoreEnvSchema = z.object({
  environmentId: guidString,
  backupId: guidString,
  targetEnvironmentId: guidString.optional(),
  confirm: z.boolean().optional().default(false),
});

const copyEnvSchema = z.object({
  sourceEnvironmentId: guidString,
  targetDisplayName: z.string().min(1).max(500),
  targetLocation: z.string().min(1).max(100),
  copyType: z.enum(["MinimalCopy", "FullCopy"]).optional().default("FullCopy"),
  confirm: z.boolean().optional().default(false),
});

const resetEnvSchema = z.object({
  environmentId: guidString,
  friendlyName: z.string().min(1).max(500),
  currencyName: z.string().max(10).optional(),
  purpose: z.string().max(2000).optional(),
  confirm: z.boolean().optional().default(false),
});

const policyIdSchema = z.object({ policyId: guidString });

const createDlpSchema = z.object({
  displayName: z.string().min(1).max(500),
  environmentType: z.enum(["AllEnvironments", "ExceptEnvironments", "OnlyEnvironments"]),
  connectorGroups: z.array(z.object({
    classification: z.enum(["Business", "NonBusiness", "Blocked"]),
    connectors: z.array(z.object({
      id: z.string().min(1).max(200),
      type: z.string().max(100).optional(),
    })).max(500),
  })).max(100),
  environments: z.array(z.object({ name: z.string().min(1).max(200) })).max(500).optional(),
});

const updateDlpSchema = z.object({
  policyId: guidString,
  displayName: z.string().max(500).optional(),
  environmentType: z.enum(["AllEnvironments", "ExceptEnvironments", "OnlyEnvironments"]).optional(),
  connectorGroups: z.array(z.object({
    classification: z.enum(["Business", "NonBusiness", "Blocked"]),
    connectors: z.array(z.object({
      id: z.string().min(1).max(200),
      type: z.string().max(100).optional(),
    })).max(500),
  })).max(100).optional(),
  environments: z.array(z.object({ name: z.string().min(1).max(200) })).max(500).optional(),
});

const deleteDlpSchema = z.object({
  policyId: guidString,
  confirm: z.boolean().optional().default(false),
});

const enableManagedSchema = z.object({
  environmentId: guidString,
  protectionLevel: z.enum(["Basic", "Standard", "Premium"]).optional().default("Standard"),
  confirm: z.boolean().optional().default(false),
});

const disableManagedSchema = z.object({
  environmentId: guidString,
  confirm: z.boolean().optional().default(false),
});

const updateManagedSchema = z.object({
  environmentId: guidString,
  protectionLevel: z.enum(["Basic", "Standard", "Premium"]).optional(),
  settings: z.record(z.string(), z.string().max(1000)).optional(),
});

// tenantId is optional everywhere: the session already knows which tenant it
// signed in to, and asking for a GUID the caller has to look up made these
// tools fail on their first use ("expected string, received undefined").
const tenantIdSchema = z.object({ tenantId: guidString.optional() });

const billingPolicySchema = z.object({
  tenantId: guidString.optional(),
  policyId: guidString,
});


// ============================================================================
// Handlers
// ============================================================================

// --- Environment Lifecycle ---

export async function handleGetEnvironment(api: PowerPlatformAdminApi, input: unknown): Promise<string> {
  const parsed = envOptionalSchema.parse(input);
  logger.info({ env: parsed.environment }, "Executing get_environment");

  const env = await api.getEnvironment(parsed.environment);
  const p = env.properties;

  const lines = [
    `Name: ${p.displayName}`,
    `ID: ${env.name}`,
    `Type: ${p.environmentSku}`,
    `Region: ${env.location ?? p.azureRegion ?? "unknown"}`,
    `State: ${p.provisioningState}`,
    `Default: ${p.isDefault}`,
    `Created: ${new Date(p.createdTime).toLocaleDateString()}`,
  ];

  if (p.linkedEnvironmentMetadata?.instanceUrl) {
    lines.push(`Dataverse URL: ${p.linkedEnvironmentMetadata.instanceUrl}`);
  }
  if (p.linkedEnvironmentMetadata?.version) {
    lines.push(`Dataverse Version: ${p.linkedEnvironmentMetadata.version}`);
  }
  if (p.description) lines.push(`Description: ${p.description}`);
  if (env.readScope === "user") {
    lines.push(
      "Read at user scope: this environment is not visible to you at admin scope (you use it but do not administer it), so admin-only details such as capacity are unavailable."
    );
  }

  return lines.join("\n");
}

export async function handleCreateEnvironment(api: PowerPlatformAdminApi, input: unknown): Promise<string> {
  const parsed = createEnvSchema.parse(input);
  logger.info({ displayName: parsed.displayName }, "Executing create_environment");

  const env = await api.createEnvironment(
    parsed.displayName,
    parsed.location,
    parsed.environmentSku,
    parsed.description,
    parsed.currencyName
  );
  return `Created environment "${env.properties.displayName}" (${env.name})`;
}

export async function handleDeleteEnvironment(api: PowerPlatformAdminApi, input: unknown): Promise<string> {
  const parsed = deleteEnvSchema.parse(input);
  logger.info({ environmentId: parsed.environmentId }, "Executing delete_environment");

  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(`Confirm delete environment ${parsed.environmentId}? THIS CANNOT BE UNDONE. Set confirm=true to proceed.`);
  }

  await api.deleteEnvironment(parsed.environmentId);
  return `Deleted environment ${parsed.environmentId}`;
}

export async function handleBackupEnvironment(api: PowerPlatformAdminApi, input: unknown): Promise<string> {
  const parsed = backupEnvSchema.parse(input);
  logger.info({ environmentId: parsed.environmentId, label: parsed.label }, "Executing backup_environment");

  const backup = await api.backupEnvironment(parsed.environmentId, parsed.label, parsed.notes);
  return `Backup created: ${backup.id ?? "pending"} - Label: ${parsed.label}`;
}

export async function handleRestoreEnvironment(api: PowerPlatformAdminApi, input: unknown): Promise<string> {
  const parsed = restoreEnvSchema.parse(input);
  logger.info({ environmentId: parsed.environmentId, backupId: parsed.backupId }, "Executing restore_environment");

  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(`Confirm restore environment ${parsed.environmentId} from backup ${parsed.backupId}? Set confirm=true.`);
  }

  const env = await api.restoreEnvironment(
    parsed.environmentId,
    parsed.backupId,
    parsed.targetEnvironmentId
  );
  return `Restore initiated for environment "${env.properties?.displayName ?? parsed.environmentId}"`;
}

export async function handleCopyEnvironment(api: PowerPlatformAdminApi, input: unknown): Promise<string> {
  const parsed = copyEnvSchema.parse(input);
  logger.info({ sourceId: parsed.sourceEnvironmentId }, "Executing copy_environment");

  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(`Confirm copy environment ${parsed.sourceEnvironmentId} as "${parsed.targetDisplayName}"? Set confirm=true.`);
  }

  const env = await api.copyEnvironment(
    parsed.sourceEnvironmentId,
    parsed.targetDisplayName,
    parsed.targetLocation,
    parsed.copyType
  );
  return `Copy initiated: "${env.properties?.displayName ?? parsed.targetDisplayName}"`;
}

export async function handleResetEnvironment(api: PowerPlatformAdminApi, input: unknown): Promise<string> {
  const parsed = resetEnvSchema.parse(input);
  logger.info({ environmentId: parsed.environmentId }, "Executing reset_environment");

  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(`Confirm RESET environment ${parsed.environmentId}? ALL DATA WILL BE DELETED. Set confirm=true.`);
  }

  const env = await api.resetEnvironment(
    parsed.environmentId,
    parsed.friendlyName,
    parsed.currencyName,
    parsed.purpose
  );
  return `Reset initiated for environment "${env.properties?.displayName ?? parsed.friendlyName}"`;
}

export async function handleListEnvironmentBackups(api: PowerPlatformAdminApi, input: unknown): Promise<string> {
  const parsed = envRequiredSchema.parse(input);
  logger.info({ environmentId: parsed.environmentId }, "Executing list_environment_backups");

  const backups = await api.listEnvironmentBackups(parsed.environmentId);

  if (backups.length === 0) {
    return "No backups found for this environment.";
  }

  // Live shape: id, label, notes, backupPointDateTime, expirationDateTime — no
  // status field, which used to print as "undefined" at the end of every line.
  const lines = [`Manual backups (${backups.length}):`];
  for (const b of backups) {
    const point = b.backupPointDateTime ?? b.createdDateTime;
    const when = point ? new Date(point).toLocaleString() : "unknown time";
    const expires = b.expirationDateTime ? `, expires ${new Date(b.expirationDateTime).toLocaleDateString()}` : "";
    const status = b.status ? `, ${b.status}` : "";
    const notes = b.notes ? ` — ${b.notes}` : "";
    lines.push(`  ${b.id} — "${b.label ?? "(unlabelled)"}" restore point ${when}${expires}${status}${notes}`);
  }
  lines.push("System (continuous) backups are not listed here; choose them by date in the admin center's Restore or manage pane.");
  return lines.join("\n");
}

// --- DLP Policies ---

export async function handleListDlpPolicies(api: PowerPlatformAdminApi, _input: unknown): Promise<string> {
  logger.info("Executing list_dlp_policies");

  const policies = await api.listDlpPolicies();

  if (policies.length === 0) {
    return "No DLP policies found.";
  }

  const lines = [`DLP Policies (${policies.length}):`];
  for (const p of policies) {
    const modified = p.properties.lastModifiedTime
      ? new Date(p.properties.lastModifiedTime).toLocaleDateString()
      : "unknown";
    lines.push(`  ${p.properties.displayName} (${p.name ?? "?"}) - ${p.properties.environmentType} - ${modified}`);
  }
  return lines.join("\n");
}

export async function handleGetDlpPolicy(api: PowerPlatformAdminApi, input: unknown): Promise<string> {
  const parsed = policyIdSchema.parse(input);
  logger.info({ policyId: parsed.policyId }, "Executing get_dlp_policy");

  const policy = await api.getDlpPolicy(parsed.policyId);
  const p = policy.properties;

  const lines = [
    `Name: ${p.displayName}`,
    `ID: ${policy.name}`,
    `Scope: ${p.environmentType}`,
    `Created: ${new Date(p.createdTime).toLocaleDateString()}`,
    `Modified: ${new Date(p.lastModifiedTime).toLocaleDateString()}`,
    "",
    "Connector Groups:",
  ];

  for (const group of p.connectorGroups ?? []) {
    lines.push(`  ${group.classification} (${group.connectors.length} connectors):`);
    for (const c of group.connectors.slice(0, 10)) {
      lines.push(`    - ${c.name ?? c.id}`);
    }
    if (group.connectors.length > 10) {
      lines.push(`    ... and ${group.connectors.length - 10} more`);
    }
  }

  return lines.join("\n");
}

export async function handleCreateDlpPolicy(api: PowerPlatformAdminApi, input: unknown): Promise<string> {
  const parsed = createDlpSchema.parse(input);
  logger.info({ displayName: parsed.displayName }, "Executing create_dlp_policy");

  const policy = await api.createDlpPolicy(
    parsed.displayName,
    parsed.environmentType,
    parsed.connectorGroups,
    parsed.environments
  );
  return `Created DLP policy "${policy.properties.displayName}" (${policy.name})`;
}

export async function handleUpdateDlpPolicy(api: PowerPlatformAdminApi, input: unknown): Promise<string> {
  const parsed = updateDlpSchema.parse(input);
  logger.info({ policyId: parsed.policyId }, "Executing update_dlp_policy");

  const updates: Record<string, unknown> = {};
  if (parsed.displayName !== undefined) updates.displayName = parsed.displayName;
  if (parsed.environmentType !== undefined) updates.environmentType = parsed.environmentType;
  if (parsed.connectorGroups !== undefined) updates.connectorGroups = parsed.connectorGroups;
  if (parsed.environments !== undefined) updates.environments = parsed.environments;

  const policy = await api.updateDlpPolicy(parsed.policyId, updates);
  return `Updated DLP policy "${policy.properties.displayName}"`;
}

export async function handleDeleteDlpPolicy(api: PowerPlatformAdminApi, input: unknown): Promise<string> {
  const parsed = deleteDlpSchema.parse(input);
  logger.info({ policyId: parsed.policyId }, "Executing delete_dlp_policy");

  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(`Confirm delete DLP policy ${parsed.policyId}? Set confirm=true to proceed.`);
  }

  await api.deleteDlpPolicy(parsed.policyId);
  return `Deleted DLP policy ${parsed.policyId}`;
}

export async function handleGetDlpConnectorConfigs(api: PowerPlatformAdminApi, input: unknown): Promise<string> {
  const parsed = policyIdSchema.parse(input);
  logger.info({ policyId: parsed.policyId }, "Executing get_dlp_connector_configs");

  const configs = await api.getDlpConnectorConfigs(parsed.policyId);

  if (configs.length === 0) {
    return "No connector configurations found for this policy.";
  }

  const lines = [`Connector Configurations (${configs.length}):`];
  for (const c of configs) {
    lines.push(`  ${c.connectorId}`);
    if (c.endpointConfigurations) {
      for (const ep of c.endpointConfigurations) {
        lines.push(`    ${ep.behavior}: ${ep.connectorEndpoint}`);
      }
    }
  }
  return lines.join("\n");
}

// --- Managed Environments ---

export async function handleGetManagedEnvironmentSettings(api: PowerPlatformAdminApi, input: unknown): Promise<string> {
  const parsed = envOptionalSchema.parse(input);
  logger.info({ env: parsed.environment }, "Executing get_managed_environment_settings");

  const config = await api.getManagedEnvironmentSettings(parsed.environment);

  const lines = [`Protection Level: ${config.protectionLevel ?? "Not managed"}`];
  if (config.settings?.extendedSettings) {
    lines.push("Extended Settings:");
    for (const [key, value] of Object.entries(config.settings.extendedSettings)) {
      lines.push(`  ${key}: ${value}`);
    }
  }
  return lines.join("\n");
}

export async function handleEnableManagedEnvironment(api: PowerPlatformAdminApi, input: unknown): Promise<string> {
  const parsed = enableManagedSchema.parse(input);
  logger.info({ environmentId: parsed.environmentId }, "Executing enable_managed_environment");

  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(`Confirm enable managed environment on ${parsed.environmentId} with ${parsed.protectionLevel} protection? Set confirm=true.`);
  }

  const config = await api.enableManagedEnvironment(parsed.environmentId, parsed.protectionLevel);
  return `Enabled managed environment (${config.protectionLevel ?? parsed.protectionLevel})`;
}

export async function handleDisableManagedEnvironment(api: PowerPlatformAdminApi, input: unknown): Promise<string> {
  const parsed = disableManagedSchema.parse(input);
  logger.info({ environmentId: parsed.environmentId }, "Executing disable_managed_environment");

  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(`Confirm disable managed environment on ${parsed.environmentId}? Set confirm=true.`);
  }

  await api.disableManagedEnvironment(parsed.environmentId);
  return `Disabled managed environment on ${parsed.environmentId}`;
}

export async function handleUpdateManagedEnvironmentSettings(api: PowerPlatformAdminApi, input: unknown): Promise<string> {
  const parsed = updateManagedSchema.parse(input);
  logger.info({ environmentId: parsed.environmentId }, "Executing update_managed_environment_settings");

  const updates: Record<string, unknown> = {};
  if (parsed.protectionLevel !== undefined) updates.protectionLevel = parsed.protectionLevel;
  if (parsed.settings !== undefined) updates.settings = { extendedSettings: parsed.settings };

  const config = await api.updateManagedEnvironmentSettings(
    parsed.environmentId,
    updates
  );
  return `Updated managed environment settings (${config.protectionLevel ?? "updated"})`;
}

// --- Capacity & Licensing ---

export async function handleGetTenantCapacity(api: PowerPlatformAdminApi, input: unknown): Promise<string> {
  tenantIdSchema.parse(input);
  logger.info("Executing get_tenant_capacity");

  const summary = await api.getTenantCapacity();

  if (summary.byType.length === 0) {
    return "No capacity data available — no environment reported a capacity record (environments without Dataverse have none).";
  }

  const lines = ["Tenant storage consumption (sum of every environment's capacity record):"];
  for (const c of summary.byType) {
    lines.push(`  ${c.capacityType}: ${c.actualConsumption} ${c.capacityUnit} actual, ${c.ratedConsumption} ${c.capacityUnit} rated`);
  }
  lines.push("", `By environment (${summary.environments.length}):`);
  for (const env of summary.environments) {
    const parts = env.capacity
      .filter((c) => c.ratedConsumption > 0 || c.actualConsumption > 0)
      .map((c) => `${c.capacityType} ${c.ratedConsumption} ${c.capacityUnit}`);
    lines.push(`  ${env.displayName} (${env.id}): ${parts.length > 0 ? parts.join(", ") : "no capacity record"}`);
  }
  lines.push(
    "",
    "Only environments you administer are included (the admin scope lists no others). Rated consumption is what counts against purchased capacity; the purchased entitlement itself is not exposed by any public Power Platform API — read it in the admin center under Licensing > Capacity."
  );
  return lines.join("\n");
}

export async function handleGetEnvironmentCapacity(api: PowerPlatformAdminApi, input: unknown): Promise<string> {
  const parsed = envOptionalSchema.parse(input);
  logger.info({ env: parsed.environment }, "Executing get_environment_capacity");

  const capacities = await api.getEnvironmentCapacity(parsed.environment);

  if (capacities.length === 0) {
    return "No capacity data available for this environment. Either it has no Dataverse database, or you are not an administrator of it — the capacity block is only returned at admin scope.";
  }

  const lines = ["Environment Capacity (storage consumed by this environment):"];
  for (const c of capacities) {
    const updated = c.updatedOn ? ` — as of ${new Date(c.updatedOn).toLocaleString()}` : "";
    lines.push(`  ${c.capacityType}: ${c.actualConsumption} ${c.capacityUnit} actual, ${c.ratedConsumption} ${c.capacityUnit} rated${updated}`);
  }
  if (capacities.some((c) => c.actualConsumption > 0 && c.ratedConsumption === 0)) {
    lines.push("Rated consumption reads 0 here: that figure is only returned to environment administrators, so this record was read at user scope.");
  }
  lines.push("Rated consumption is what counts against the tenant's purchased capacity; use get_tenant_capacity for the tenant totals.");
  return lines.join("\n");
}

export async function handleListBillingPolicies(api: PowerPlatformAdminApi, input: unknown): Promise<string> {
  tenantIdSchema.parse(input);
  logger.info("Executing list_billing_policies");

  const policies = await api.listBillingPolicies();

  if (policies.length === 0) {
    return "No billing policies found.";
  }

  const lines = [`Billing Policies (${policies.length}):`];
  for (const p of policies) {
    lines.push(`  ${p.name} (${p.id ?? "?"}) - ${p.status} - ${p.location}`);
  }
  return lines.join("\n");
}

export async function handleGetBillingPolicy(api: PowerPlatformAdminApi, input: unknown): Promise<string> {
  const parsed = billingPolicySchema.parse(input);
  logger.info({ policyId: parsed.policyId }, "Executing get_billing_policy");

  const policy = await api.getBillingPolicy(parsed.policyId);

  const lines = [
    `Name: ${policy.name}`,
    `ID: ${policy.id}`,
    `Status: ${policy.status}`,
    `Location: ${policy.location}`,
  ];
  if (policy.billingInstrument) {
    lines.push(`Subscription: ${policy.billingInstrument.subscriptionId}`);
    lines.push(`Resource Group: ${policy.billingInstrument.resourceGroup}`);
  }
  return lines.join("\n");
}

export async function handleGetApiRequestSummary(_api: PowerPlatformAdminApi, input: unknown): Promise<string> {
  tenantIdSchema.parse(input);
  logger.info("Executing get_api_request_summary");
  // The previous implementation called /scopes/admin/tenants/{id}/apiRequestSummary,
  // a resource that does not exist (404 on every call). No public Power Platform API
  // exposes request consumption; saying so beats inventing a number.
  return [
    "Power Platform request (API call) consumption is not exposed by any public Power Platform API — neither BAP nor api.powerplatform.com has a request-summary resource, so this tool cannot fetch it.",
    "Where it lives: Power Platform admin center > Licensing > Power Platform requests (per-user and per-flow reports, downloadable).",
    "What this server CAN report: storage capacity per environment (get_environment_capacity) and across the tenant (get_tenant_capacity), and pay-as-you-go billing policies (list_billing_policies).",
  ].join("\n");
}

// ============================================================================
// All admin tool definitions
// ============================================================================

export const adminTools: Tool[] = [
  // Environment lifecycle
  getEnvironmentTool,
  createEnvironmentTool,
  deleteEnvironmentTool,
  backupEnvironmentTool,
  restoreEnvironmentTool,
  copyEnvironmentTool,
  resetEnvironmentTool,
  listEnvironmentBackupsTool,
  // DLP
  listDlpPoliciesTool,
  getDlpPolicyTool,
  createDlpPolicyTool,
  updateDlpPolicyTool,
  deleteDlpPolicyTool,
  getDlpConnectorConfigsTool,
  // Managed environments
  getManagedEnvironmentSettingsTool,
  enableManagedEnvironmentTool,
  disableManagedEnvironmentTool,
  updateManagedEnvironmentSettingsTool,
  // Capacity & licensing
  getTenantCapacityTool,
  getEnvironmentCapacityTool,
  listBillingPoliciesTool,
  getBillingPolicyTool,
  getApiRequestSummaryTool,
];
